<template>
	<li class="sidebar-item" v-if="canViewMenu(props.item.route.name) && canViewChildren()">
		<div class="flex group flex-row lg:flex-col" @mouseleave="hideOnMouseLeave">
			<div
				class="flex group cursor-pointer w-17.5 lg:w-full mx-auto rounded duration-300 transition-colors ease-in-out leading-5 lg:hover:bg-primary-darker"
				@click.prevent="handleItemClick"
				:class="{ 'lg:bg-primary-darker': isSelected }"
			>
				<div class="flex flex-col lg:flex-row w-fit mx-auto lg:mx-5 lg:h-8 lg:items-center">
					<img
						:alt="props.item.label"
						:title="props.item.label"
						:src="getImageUrl()"
						class="mx-auto py-2 px-4 rounded group-hover:bg-primary-darker lg:p-0 lg:mb-0 lg:mr-2 lg:group-hover:bg-transparent"
						:class="{ 'bg-primary-darker': isSelected, [props.iconClass]:true }"
					/>

					<div class="w-full text-label-on-color text-xs font-semibold lg:text-sm text-center">
						{{ props.item.label }}
					</div>
				</div>

				<!-- Unread count is not used
				<span
					v-if="props.item.unreadCount"
					class="absolute right-4 block rounded bg-lo-warning-on-color py-1 px-2 text-xs font-medium text-label-on-color"
				>
          {{ props.item.unreadCount }}
        </span>
        -->

				<img
					src="@/assets/images/icon/dashboard/white/chevron-right.svg"
					alt="Toggle"
					title="Click to expand/collapse"
					v-if="props.item.children"
					class="ml-auto size-5 text-label-on-color hidden transform duration-200 lg:block lg:my-auto lg:mr-2"
					:class="{ 'rotate-90': expanded }"
					aria-hidden="true"
				/>
			<!--
				Removed this b/c not sure why we'd show the arrow if there's no
				<img
					src="@/assets/images/icon/dashboard/white/chevron-right.svg"
					alt=">"
					v-else
					class="hidden ml-auto size-5 text-label-on-color lg:block lg:my-auto lg:mr-2"
				/>-->
			</div>

			<!--      <transition name="fade" mode="out-in">-->
			<!--"absolute left-20 bg-primary rounded-lg hidden lg:block group-hover:block lg:static lg:translate lg:transform lg:overflow-hidden"
			 v-show="expanded"
			-->
			<div
				v-if="Util.objHasArray(props.item, 'children')"
				@click="toggleExpanded(!expanded)"
				@mouseleave="hideOnMouseLeave"
				:class="{
          'sidebar-dropdown absolute left-20 bg-primary rounded-lg lg:static lg:translate lg:transform lg:overflow-hidden': true,
          'lg:block ': expanded,
          hidden: !expanded
        }"
			>
				<SidebarDropdown :items="props.item.children" />
			</div>
			<!--      </transition>-->
		</div>
	</li>
</template>

<script setup lang="js">
import SidebarDropdown from './SidebarDropdown.vue'
import { SidebarMenuItem } from '@/models/SidebarMenuItem.js'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import router from '@/router/router.js'
import { AppSettings } from '@/models/enums/AppSettings.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import { Util } from '@/models/utils/Util.js'
import { useBroadcastChannel } from '@vueuse/core'

const channel = useBroadcastChannel({ name: 'NavigationCancel' })
const expanded = ref(false)

onMounted(() =>
{
	showChildren()
	window.addEventListener('resize', handleResize)
})

/**
 * Watch for navigation onBeforeRouteLeave capture to keep 'Admin Center' open.
 * Without this, when navigating to a different page from a page with changes,
 * the @click="toggleExpanded(!expanded)" gets triggered, minimizing the Admin Center.
 * Example: OrganizationRoleEditPage.vue
 */
watch(channel.data,
	/**
	 * Handles post from filter bar search
	 * @param {{ filter: Object }} p_data Filter object
	 */
	() => {showChildren()});

onUnmounted(() =>
{
	window.removeEventListener('resize', handleResize)
	channel.close()
})

const props = defineProps({
	item: { type: SidebarMenuItem, default: '', children: [] },
	iconClass: { type: String, default: '' },
})

const isSelected = computed(() =>
{
	return router.currentRoute.value.name === props.item.route.name
})


/**
 * Shows/Hides the sub menu
 * @param p_expand_eh {boolean} True to show, false to hide
 */
function toggleExpanded(p_expand_eh)
{
	//console.log('toggleExpanded', p_expand_eh, props.item)
	//debugger;
	expanded.value = p_expand_eh
}

/**
 * Determines if the children should be shown or not based on the current route
 * and current screen size
 */
function showChildren()
{
	//Children are hidden for smalls screens
	if (window.innerWidth >= AppSettings.SCREENS.lg)
	{
		toggleExpanded(hasRouteName(props.item, router.currentRoute.value.name))
	}
	else
	{
		toggleExpanded(false)
	}
}

/**
 * Recursively checks if the item or its children has the given route name.
 * @param p_item
 * @param p_route_name
 * @returns {*|boolean}
 */
function hasRouteName(p_item, p_route_name)
{
	if (p_item.route?.name === p_route_name) return true;
	if (p_item.children) {
		return p_item.children.some(child => hasRouteName(child, p_route_name));
	}
	return false;
}

/**
 * Hide the submenu if the mouse leaves the area, but only when showing mobile screen
 */
function hideOnMouseLeave()
{
	if (window.innerWidth < AppSettings.SCREENS.lg)
	{
		toggleExpanded(false)
	}
}

/**
 * Handles selecting the main (or parent) menu item.
 * Navigates user to page.
 */
const handleItemClick = () =>
{
	toggleExpanded(props.item.children && !expanded.value)

	if (props.item.route)
	{
		router.push(props.item.route)
	}
}

/**
 * Gets the url for the menu item icon
 * @returns {URL}
 */
function getImageUrl()
{
	return new URL(`/src/assets/images/icon/${props.item.icon}`, import.meta.url)
}

/**
 * Shows/Hides the submenu based on the size of the screen when the user resizes.
 */
const handleResize = () =>
{
	if (window.innerWidth < AppSettings.SCREENS.lg)
	{
		toggleExpanded(false)
	}
	else
	{
		showChildren()
	}
}

/**
 * Checks if any of the children are visible to the user based on tenant or hidden property
 * @returns {boolean} True if visible or item has no children
 */
function canViewChildren()
{
	//If there are no children or [] was passed in, then children play no role in whether the menu item can be viewed
	if (props.item.children === null || props.item.children.length === 0) return true

	const children = props.item.children.filter((child) => canViewMenu(child.route.name) && !child.hidden)
	return children.length > 0
}

/**
 * Checks if a menu item is disabled for a tenant.
 * @param {string} p_route_name Route name
 * @returns {boolean} True if route is not disabled for tenant
 */
function canViewMenu(p_route_name)
{
	return Tenants.isModuleEnabled(p_route_name)
}

</script>
